/**
 * Mock dataset — a realistic Inditech corpus used while VITE_API_MODE=mock.
 *
 * The shapes here match the API contracts in docs/02-api-architecture.md exactly, so
 * swapping to the live backend is a transport change only. Permissions are *data*, never
 * constants in components (UX Rule 4 / Rule 10).
 */

const now = Date.now();
const MIN = 60_000;
const HOUR = 60 * MIN;
const DAY = 24 * HOUR;

const minutesAgo = (n) => new Date(now - n * MIN).toISOString();
const hoursAgo = (n) => new Date(now - n * HOUR).toISOString();
const daysAgo = (n) => new Date(now - n * DAY).toISOString();
const daysAhead = (n) => new Date(now + n * DAY).toISOString();

/* ------------------------------------------------------------------ permissions */

export const permissions = [
  { key: 'DOCUMENT_VIEW', name: 'View documents', category: 'documents', description: 'Open and preview documents they have access to.' },
  { key: 'DOCUMENT_UPLOAD', name: 'Upload documents', category: 'documents', description: 'Add new documents to folders they can write to.' },
  { key: 'DOCUMENT_DOWNLOAD', name: 'Download documents', category: 'documents', description: 'Download a local copy of a document.' },
  { key: 'DOCUMENT_EDIT', name: 'Edit metadata', category: 'documents', description: 'Rename, describe and tag documents.' },
  { key: 'DOCUMENT_REPLACE', name: 'Replace version', category: 'documents', description: 'Upload a new version over an existing document.' },
  { key: 'DOCUMENT_DELETE', name: 'Delete documents', category: 'documents', description: 'Move documents to Trash.', dangerous: true },
  { key: 'DOCUMENT_DELETE_PERMANENT', name: 'Delete permanently', category: 'documents', description: 'Purge documents from Trash. Cannot be undone.', dangerous: true },
  { key: 'FOLDER_CREATE', name: 'Create folders', category: 'folders', description: 'Create folders and sub-folders.' },
  { key: 'FOLDER_RENAME', name: 'Rename folders', category: 'folders', description: 'Rename existing folders.' },
  { key: 'FOLDER_MOVE', name: 'Move items', category: 'folders', description: 'Move documents and folders between locations.' },
  { key: 'FOLDER_DELETE', name: 'Delete folders', category: 'folders', description: 'Move folders and their contents to Trash.', dangerous: true },
  { key: 'DOCUMENT_SHARE', name: 'Share internally', category: 'sharing', description: 'Share documents with colleagues and departments.' },
  { key: 'DOCUMENT_SHARE_EXTERNAL', name: 'Create external links', category: 'sharing', description: 'Create secure links for people outside Inditech.', dangerous: true },
  { key: 'SEARCH_AI', name: 'Intelligent search', category: 'search', description: 'Use natural-language document search.' },
  { key: 'ADMIN_USER_MANAGE', name: 'Manage users', category: 'administration', description: 'Invite, edit and deactivate users.', dangerous: true },
  { key: 'ADMIN_ROLE_MANAGE', name: 'Manage roles & permissions', category: 'administration', description: 'Change what each role can do.', dangerous: true },
  { key: 'ADMIN_DEPARTMENT_MANAGE', name: 'Manage departments', category: 'administration', description: 'Create and configure departments.' },
  { key: 'ADMIN_AUDIT_VIEW', name: 'View audit logs', category: 'administration', description: 'Read the organisation-wide audit trail.' },
  { key: 'ADMIN_STORAGE_VIEW', name: 'View storage & processing', category: 'administration', description: 'Monitor storage usage and the OCR pipeline.' },
  { key: 'ADMIN_SETTINGS_MANAGE', name: 'Manage system settings', category: 'administration', description: 'Change sharing, retention and security policy.', dangerous: true },
  { key: 'ADMIN_DOCUMENT_OVERRIDE', name: 'Override document access', category: 'administration', description: 'Access any document in the organisation. Always audited.', dangerous: true },
];

/* ------------------------------------------------------------------------ roles */
/* This matrix is the seed of role_permissions in PostgreSQL — the UI only reads it. */

const ALL = permissions.map((p) => p.key);

export const roles = [
  {
    id: 'role-admin',
    key: 'SYSTEM_ADMIN',
    name: 'System Administrator',
    description: 'Full control of the Document Hub, including users, roles and policy.',
    isSystem: true,
    rank: 1,
    permissions: ALL,
  },
  {
    id: 'role-dept-manager',
    key: 'DEPARTMENT_MANAGER',
    name: 'Department Manager',
    description: 'Manages all documents and members within their department.',
    isSystem: true,
    rank: 2,
    permissions: [
      'DOCUMENT_VIEW', 'DOCUMENT_UPLOAD', 'DOCUMENT_DOWNLOAD', 'DOCUMENT_EDIT', 'DOCUMENT_REPLACE',
      'DOCUMENT_DELETE', 'FOLDER_CREATE', 'FOLDER_RENAME', 'FOLDER_MOVE', 'FOLDER_DELETE',
      'DOCUMENT_SHARE', 'DOCUMENT_SHARE_EXTERNAL', 'SEARCH_AI', 'ADMIN_DEPARTMENT_MANAGE',
    ],
  },
  {
    id: 'role-manager',
    key: 'MANAGER',
    name: 'Manager',
    description: 'Manages documents for their team, without department administration.',
    isSystem: true,
    rank: 3,
    permissions: [
      'DOCUMENT_VIEW', 'DOCUMENT_UPLOAD', 'DOCUMENT_DOWNLOAD', 'DOCUMENT_EDIT', 'DOCUMENT_REPLACE',
      'DOCUMENT_DELETE', 'FOLDER_CREATE', 'FOLDER_RENAME', 'FOLDER_MOVE', 'DOCUMENT_SHARE', 'SEARCH_AI',
    ],
  },
  {
    id: 'role-executive',
    key: 'EXECUTIVE',
    name: 'Executive',
    description: 'Day-to-day contributor — uploads, downloads and shares documents.',
    isSystem: true,
    rank: 4,
    permissions: [
      'DOCUMENT_VIEW', 'DOCUMENT_UPLOAD', 'DOCUMENT_DOWNLOAD', 'DOCUMENT_EDIT',
      'FOLDER_CREATE', 'DOCUMENT_SHARE', 'SEARCH_AI',
    ],
  },
  {
    id: 'role-viewer',
    key: 'VIEWER',
    name: 'Viewer',
    description: 'Read-only access to documents shared with them or their department.',
    isSystem: true,
    rank: 5,
    permissions: ['DOCUMENT_VIEW'],
  },
];

/* ------------------------------------------------------------------ departments */

export const departments = [
  { id: 'dep-sales', name: 'Sales', code: 'SALES', color: '#1B4DB1', description: 'Regional sales, proposals and customer agreements.', status: 'active', createdAt: daysAgo(900) },
  { id: 'dep-marketing', name: 'Marketing', code: 'MKT', color: '#7C3FBF', description: 'Brand, campaigns, collateral and events.', status: 'active', createdAt: daysAgo(900) },
  { id: 'dep-finance', name: 'Finance', code: 'FIN', color: '#0F7B4F', description: 'Budgets, invoices, audits and statutory filings.', status: 'active', createdAt: daysAgo(900) },
  { id: 'dep-hr', name: 'Human Resources', code: 'HR', color: '#C2571B', description: 'Policies, employee records and recruitment.', status: 'active', createdAt: daysAgo(900) },
  { id: 'dep-it', name: 'Information Technology', code: 'IT', color: '#0E9E8E', description: 'Infrastructure, licensing, security and support.', status: 'active', createdAt: daysAgo(900) },
  { id: 'dep-ops', name: 'Operations', code: 'OPS', color: '#B25E09', description: 'Service delivery, vendors and logistics.', status: 'active', createdAt: daysAgo(760) },
  { id: 'dep-legal', name: 'Legal', code: 'LEG', color: '#5B6472', description: 'Contracts, compliance and corporate governance.', status: 'active', createdAt: daysAgo(620) },
  { id: 'dep-projects', name: 'Projects', code: 'PRJ', color: '#C0342B', description: 'Delivery programmes and implementation records.', status: 'active', createdAt: daysAgo(540) },
];

/* ------------------------------------------------------------------------ users */

export const users = [
  { id: 'usr-vaisakh', name: 'Vaisakh Menon', email: 'vaisakh@inditechme.com', jobTitle: 'Head of Technology', departmentId: 'dep-it', roleId: 'role-admin', status: 'active', mfaEnabled: true, lastLoginAt: minutesAgo(4), createdAt: daysAgo(880) },
  { id: 'usr-sareesh', name: 'Sareesh Kumar', email: 'sareesh@inditech.me', jobTitle: 'Sales Manager — GCC', departmentId: 'dep-sales', roleId: 'role-dept-manager', status: 'active', mfaEnabled: true, lastLoginAt: hoursAgo(2), createdAt: daysAgo(860) },
  { id: 'usr-riyas', name: 'Riyas Ahmed', email: 'riyas@inditech.me', jobTitle: 'Marketing Manager', departmentId: 'dep-marketing', roleId: 'role-dept-manager', status: 'active', mfaEnabled: false, lastLoginAt: hoursAgo(5), createdAt: daysAgo(840) },
  { id: 'usr-mohsin', name: 'Mohsin Ali', email: 'mohsin@inditech.me', jobTitle: 'Project Manager', departmentId: 'dep-projects', roleId: 'role-manager', status: 'active', mfaEnabled: true, lastLoginAt: hoursAgo(9), createdAt: daysAgo(520) },
  { id: 'usr-anjali', name: 'Anjali Nair', email: 'anjali@inditech.me', jobTitle: 'Finance Controller', departmentId: 'dep-finance', roleId: 'role-manager', status: 'active', mfaEnabled: true, lastLoginAt: daysAgo(1), createdAt: daysAgo(700) },
  { id: 'usr-fathima', name: 'Fathima Rahman', email: 'fathima@inditech.me', jobTitle: 'HR Manager', departmentId: 'dep-hr', roleId: 'role-dept-manager', status: 'active', mfaEnabled: true, lastLoginAt: hoursAgo(26), createdAt: daysAgo(760) },
  { id: 'usr-priya', name: 'Priya Raghavan', email: 'priya@inditech.me', jobTitle: 'Legal Counsel', departmentId: 'dep-legal', roleId: 'role-manager', status: 'active', mfaEnabled: true, lastLoginAt: daysAgo(2), createdAt: daysAgo(600) },
  { id: 'usr-deepak', name: 'Deepak Shetty', email: 'deepak@inditech.me', jobTitle: 'Operations Lead', departmentId: 'dep-ops', roleId: 'role-executive', status: 'active', mfaEnabled: false, lastLoginAt: hoursAgo(30), createdAt: daysAgo(480) },
  { id: 'usr-nithin', name: 'Nithin Joseph', email: 'nithin@inditech.me', jobTitle: 'Sales Executive', departmentId: 'dep-sales', roleId: 'role-executive', status: 'active', mfaEnabled: false, lastLoginAt: hoursAgo(7), createdAt: daysAgo(420) },
  { id: 'usr-aisha', name: 'Aisha Khan', email: 'aisha@inditech.me', jobTitle: 'Content Specialist', departmentId: 'dep-marketing', roleId: 'role-executive', status: 'active', mfaEnabled: false, lastLoginAt: hoursAgo(20), createdAt: daysAgo(380) },
  { id: 'usr-rahul', name: 'Rahul Varma', email: 'rahul@inditech.me', jobTitle: 'Systems Engineer', departmentId: 'dep-it', roleId: 'role-executive', status: 'active', mfaEnabled: true, lastLoginAt: hoursAgo(3), createdAt: daysAgo(360) },
  { id: 'usr-sneha', name: 'Sneha Pillai', email: 'sneha@inditech.me', jobTitle: 'Accounts Executive', departmentId: 'dep-finance', roleId: 'role-executive', status: 'active', mfaEnabled: false, lastLoginAt: daysAgo(3), createdAt: daysAgo(300) },
  { id: 'usr-tariq', name: 'Tariq Hassan', email: 'tariq@inditech.me', jobTitle: 'Implementation Consultant', departmentId: 'dep-projects', roleId: 'role-executive', status: 'active', mfaEnabled: false, lastLoginAt: hoursAgo(12), createdAt: daysAgo(260) },
  { id: 'usr-meera', name: 'Meera Suresh', email: 'meera@inditech.me', jobTitle: 'HR Coordinator', departmentId: 'dep-hr', roleId: 'role-viewer', status: 'active', mfaEnabled: false, lastLoginAt: daysAgo(4), createdAt: daysAgo(180) },
  { id: 'usr-arun', name: 'Arun Krishnan', email: 'arun@inditech.me', jobTitle: 'Logistics Assistant', departmentId: 'dep-ops', roleId: 'role-viewer', status: 'active', mfaEnabled: false, lastLoginAt: daysAgo(6), createdAt: daysAgo(150) },
  { id: 'usr-zainab', name: 'Zainab Sheikh', email: 'zainab@inditech.me', jobTitle: 'Sales Support', departmentId: 'dep-sales', roleId: 'role-viewer', status: 'invited', mfaEnabled: false, lastLoginAt: null, createdAt: daysAgo(9) },
  { id: 'usr-george', name: 'George Thomas', email: 'george@inditech.me', jobTitle: 'Former Consultant', departmentId: 'dep-projects', roleId: 'role-viewer', status: 'suspended', mfaEnabled: false, lastLoginAt: daysAgo(64), createdAt: daysAgo(700) },
];

/* ---------------------------------------------------------------------- folders */

const F = (id, name, departmentId, parentId, ownerId, createdDays) => ({
  id,
  name,
  departmentId,
  parentId,
  ownerId,
  type: 'folder',
  createdAt: daysAgo(createdDays),
  updatedAt: daysAgo(Math.max(0, createdDays - 30)),
  deletedAt: null,
});

export const folders = [
  // department roots
  F('fld-sales', 'Sales', 'dep-sales', null, 'usr-sareesh', 880),
  F('fld-marketing', 'Marketing', 'dep-marketing', null, 'usr-riyas', 880),
  F('fld-finance', 'Finance', 'dep-finance', null, 'usr-anjali', 880),
  F('fld-hr', 'Human Resources', 'dep-hr', null, 'usr-fathima', 880),
  F('fld-it', 'Information Technology', 'dep-it', null, 'usr-vaisakh', 880),
  F('fld-ops', 'Operations', 'dep-ops', null, 'usr-deepak', 760),
  F('fld-legal', 'Legal', 'dep-legal', null, 'usr-priya', 620),
  F('fld-projects', 'Projects', 'dep-projects', null, 'usr-mohsin', 540),
  // sales
  F('fld-sales-uae', 'UAE', 'dep-sales', 'fld-sales', 'usr-sareesh', 820),
  F('fld-sales-ksa', 'KSA', 'dep-sales', 'fld-sales', 'usr-sareesh', 800),
  F('fld-sales-qatar', 'Qatar', 'dep-sales', 'fld-sales', 'usr-nithin', 640),
  F('fld-sales-pricing', 'Price Lists', 'dep-sales', 'fld-sales', 'usr-sareesh', 700),
  F('fld-sales-uae-prop', 'Proposals', 'dep-sales', 'fld-sales-uae', 'usr-sareesh', 810),
  F('fld-sales-uae-contracts', 'Contracts', 'dep-sales', 'fld-sales-uae', 'usr-sareesh', 810),
  F('fld-sales-uae-customers', 'Customers', 'dep-sales', 'fld-sales-uae', 'usr-nithin', 780),
  F('fld-sales-ksa-prop', 'Proposals', 'dep-sales', 'fld-sales-ksa', 'usr-nithin', 760),
  F('fld-sales-qatar-prop', 'Proposals', 'dep-sales', 'fld-sales-qatar', 'usr-nithin', 620),
  // marketing
  F('fld-mkt-brand', 'Brand Assets', 'dep-marketing', 'fld-marketing', 'usr-riyas', 840),
  F('fld-mkt-campaigns', 'Campaigns', 'dep-marketing', 'fld-marketing', 'usr-aisha', 700),
  F('fld-mkt-profiles', 'Company Profiles', 'dep-marketing', 'fld-marketing', 'usr-riyas', 820),
  F('fld-mkt-events', 'Events', 'dep-marketing', 'fld-marketing', 'usr-aisha', 500),
  // finance
  F('fld-fin-invoices', 'Invoices', 'dep-finance', 'fld-finance', 'usr-sneha', 860),
  F('fld-fin-budgets', 'Budgets', 'dep-finance', 'fld-finance', 'usr-anjali', 860),
  F('fld-fin-audit', 'Audit Reports', 'dep-finance', 'fld-finance', 'usr-anjali', 800),
  F('fld-fin-tax', 'Tax & Statutory', 'dep-finance', 'fld-finance', 'usr-anjali', 700),
  // hr
  F('fld-hr-policies', 'Policies', 'dep-hr', 'fld-hr', 'usr-fathima', 840),
  F('fld-hr-records', 'Employee Records', 'dep-hr', 'fld-hr', 'usr-fathima', 840),
  F('fld-hr-recruitment', 'Recruitment', 'dep-hr', 'fld-hr', 'usr-meera', 600),
  // it
  F('fld-it-infra', 'Infrastructure', 'dep-it', 'fld-it', 'usr-vaisakh', 800),
  F('fld-it-licenses', 'Licences', 'dep-it', 'fld-it', 'usr-rahul', 780),
  F('fld-it-security', 'Security', 'dep-it', 'fld-it', 'usr-vaisakh', 640),
  // ops
  F('fld-ops-sop', 'SOPs', 'dep-ops', 'fld-ops', 'usr-deepak', 700),
  F('fld-ops-vendors', 'Vendors', 'dep-ops', 'fld-ops', 'usr-deepak', 620),
  // legal
  F('fld-legal-agreements', 'Agreements', 'dep-legal', 'fld-legal', 'usr-priya', 600),
  F('fld-legal-compliance', 'Compliance', 'dep-legal', 'fld-legal', 'usr-priya', 560),
  // projects
  F('fld-prj-landmark', 'Landmark Mall', 'dep-projects', 'fld-projects', 'usr-mohsin', 400),
  F('fld-prj-egoal', 'EGoal Rollout', 'dep-projects', 'fld-projects', 'usr-tariq', 320),
  F('fld-prj-stori', 'Stori Deployment', 'dep-projects', 'fld-projects', 'usr-mohsin', 260),
];

/* -------------------------------------------------------------------- documents */

/** [folderId, name, ownerId, sizeMB, modifiedDaysAgo, tags, confidential] */
const DOC_SPECS = [
  ['fld-sales-uae-prop', 'EGoal Proposal.pdf', 'usr-sareesh', 4.2, 0.02, ['proposal', 'egoal'], false],
  ['fld-sales-uae-prop', 'Landmark Mall Proposal.pdf', 'usr-sareesh', 3.6, 6, ['proposal', 'landmark'], false],
  ['fld-sales-uae-prop', 'Stori Retail Proposal.pptx', 'usr-nithin', 12.4, 11, ['proposal', 'stori'], false],
  ['fld-sales-uae-prop', 'Al Bustan Tower Proposal.pdf', 'usr-nithin', 2.9, 24, ['proposal'], false],
  ['fld-sales-uae-prop', 'Proposal Template 2026.docx', 'usr-sareesh', 0.6, 48, ['template'], false],
  ['fld-sales-uae-contracts', 'Landmark Mall Agreement.pdf', 'usr-sareesh', 2.8, 4, ['agreement', 'landmark'], true],
  ['fld-sales-uae-contracts', 'Emirates Retail Group Contract.pdf', 'usr-sareesh', 3.1, 19, ['agreement'], true],
  ['fld-sales-uae-contracts', 'Marina Plaza AMC Agreement.pdf', 'usr-nithin', 1.9, 33, ['agreement', 'amc'], false],
  ['fld-sales-uae-contracts', 'Landmark Renewal 2026.pdf', 'usr-sareesh', 1.4, 9, ['agreement', 'landmark', 'renewal'], false],
  ['fld-sales-uae-customers', 'Customer Master List.xlsx', 'usr-nithin', 1.8, 2, ['reference'], false],
  ['fld-sales-uae-customers', 'Emirates Retail Group Profile.docx', 'usr-nithin', 0.9, 27, [], false],
  ['fld-sales-uae-customers', 'Landmark Group Contacts.xlsx', 'usr-zainab', 0.4, 41, [], false],
  ['fld-sales-ksa-prop', 'Riyadh Expansion Proposal.pdf', 'usr-nithin', 5.3, 14, ['proposal'], false],
  ['fld-sales-ksa-prop', 'Jeddah Retail Proposal.pdf', 'usr-sareesh', 4.1, 38, ['proposal'], false],
  ['fld-sales-qatar-prop', 'Doha Festival City Proposal.pdf', 'usr-nithin', 3.3, 21, ['proposal'], false],
  ['fld-sales-pricing', 'GCC Price List 2026.xlsx', 'usr-sareesh', 2.2, 7, ['pricing'], true],
  ['fld-sales-pricing', 'Partner Discount Matrix.xlsx', 'usr-sareesh', 0.7, 52, ['pricing'], true],
  ['fld-sales', 'Sales Report Q3 2026.xlsx', 'usr-riyas', 1.4, 6, ['report'], false],
  ['fld-sales', 'Pipeline Review September.pptx', 'usr-sareesh', 8.7, 3, ['report'], false],

  ['fld-mkt-profiles', 'Company Profile.pdf', 'usr-riyas', 6.8, 1, ['brand'], false],
  ['fld-mkt-profiles', 'Inditech Capability Statement.pdf', 'usr-riyas', 4.4, 16, ['brand'], false],
  ['fld-mkt-profiles', 'Company Profile — Arabic.pdf', 'usr-aisha', 7.1, 29, ['brand'], false],
  ['fld-mkt-brand', 'Inditech Logo Pack.zip', 'usr-riyas', 18.6, 63, ['brand'], false],
  ['fld-mkt-brand', 'Brand Guidelines 2026.pdf', 'usr-riyas', 11.2, 44, ['brand'], false],
  ['fld-mkt-brand', 'Primary Logo.png', 'usr-aisha', 0.5, 63, ['brand'], false],
  ['fld-mkt-campaigns', 'Q4 Campaign Plan.pptx', 'usr-aisha', 9.4, 5, ['campaign'], false],
  ['fld-mkt-campaigns', 'Digital Ad Performance.xlsx', 'usr-aisha', 1.1, 12, ['campaign', 'report'], false],
  ['fld-mkt-campaigns', 'Social Media Calendar.xlsx', 'usr-aisha', 0.8, 2, ['campaign'], false],
  ['fld-mkt-events', 'GITEX 2026 Plan.docx', 'usr-riyas', 1.6, 18, ['event'], false],
  ['fld-mkt-events', 'Booth Design Concept.png', 'usr-aisha', 5.2, 22, ['event'], false],

  ['fld-fin-budgets', 'Annual Budget 2026.xlsx', 'usr-anjali', 3.4, 10, ['budget'], true],
  ['fld-fin-budgets', 'Department Budget Allocation.xlsx', 'usr-anjali', 1.2, 31, ['budget'], true],
  ['fld-fin-invoices', 'Invoice Register September.xlsx', 'usr-sneha', 2.6, 1, ['invoice'], false],
  ['fld-fin-invoices', 'Landmark Mall Invoice 4471.pdf', 'usr-sneha', 0.3, 8, ['invoice', 'landmark'], false],
  ['fld-fin-invoices', 'Vendor Payment Schedule.xlsx', 'usr-sneha', 0.9, 15, ['invoice'], false],
  ['fld-fin-audit', 'Internal Audit Report 2026.pdf', 'usr-anjali', 5.7, 26, ['audit'], true],
  ['fld-fin-audit', 'Statutory Audit FY2025.pdf', 'usr-anjali', 8.2, 190, ['audit'], true],
  ['fld-fin-tax', 'VAT Return Q2 2026.pdf', 'usr-sneha', 0.7, 62, ['tax'], true],
  ['fld-fin-tax', 'Corporate Tax Registration.pdf', 'usr-anjali', 1.1, 240, ['tax'], true],
  ['fld-finance', 'Landmark AMC Agreement.pdf', 'usr-anjali', 2.1, 13, ['agreement', 'landmark', 'amc'], false],

  ['fld-hr-policies', 'Employee Handbook 2026.pdf', 'usr-fathima', 4.9, 20, ['policy'], false],
  ['fld-hr-policies', 'Leave Policy.pdf', 'usr-fathima', 0.8, 35, ['policy'], false],
  ['fld-hr-policies', 'Code of Conduct.pdf', 'usr-fathima', 1.3, 35, ['policy'], false],
  ['fld-hr-policies', 'Remote Working Policy.docx', 'usr-meera', 0.5, 8, ['policy'], false],
  ['fld-hr-records', 'Employee Master Register.xlsx', 'usr-fathima', 2.4, 3, ['confidential'], true],
  ['fld-hr-records', 'Payroll Summary September.xlsx', 'usr-fathima', 1.1, 4, ['confidential'], true],
  ['fld-hr-recruitment', 'Job Description — Sales Executive.docx', 'usr-meera', 0.4, 28, ['recruitment'], false],
  ['fld-hr-recruitment', 'Interview Scorecard Template.xlsx', 'usr-meera', 0.3, 70, ['template'], false],

  ['fld-it-infra', 'Network Architecture.pdf', 'usr-vaisakh', 3.8, 45, ['architecture'], false],
  ['fld-it-infra', 'AWS Account Structure.pdf', 'usr-rahul', 1.9, 17, ['architecture', 'aws'], false],
  ['fld-it-infra', 'Server Inventory.xlsx', 'usr-rahul', 0.6, 9, ['reference'], false],
  ['fld-it-licenses', 'Microsoft 365 Licence Agreement.pdf', 'usr-rahul', 1.2, 55, ['licence'], false],
  ['fld-it-licenses', 'Software Licence Register.xlsx', 'usr-rahul', 0.8, 23, ['licence'], false],
  ['fld-it-security', 'Information Security Policy.pdf', 'usr-vaisakh', 2.7, 30, ['policy', 'security'], true],
  ['fld-it-security', 'Access Control Matrix.xlsx', 'usr-vaisakh', 0.9, 11, ['security'], true],
  ['fld-it-security', 'Penetration Test Report 2026.pdf', 'usr-vaisakh', 6.3, 40, ['security'], true],

  ['fld-ops-sop', 'Service Delivery SOP.pdf', 'usr-deepak', 2.2, 25, ['sop'], false],
  ['fld-ops-sop', 'Installation Checklist.docx', 'usr-deepak', 0.4, 37, ['sop'], false],
  ['fld-ops-vendors', 'Vendor Master List.xlsx', 'usr-deepak', 1.3, 14, ['reference'], false],
  ['fld-ops-vendors', 'Logistics Partner Agreement.pdf', 'usr-arun', 1.7, 58, ['agreement'], false],

  ['fld-legal-agreements', 'Master Services Agreement Template.docx', 'usr-priya', 0.9, 50, ['template', 'agreement'], false],
  ['fld-legal-agreements', 'NDA Template.docx', 'usr-priya', 0.3, 50, ['template'], false],
  ['fld-legal-agreements', 'Landmark Mall Legal Review.pdf', 'usr-priya', 1.5, 7, ['landmark', 'agreement'], true],
  ['fld-legal-compliance', 'Data Protection Compliance Report.pdf', 'usr-priya', 3.2, 33, ['compliance'], true],
  ['fld-legal-compliance', 'Trade Licence 2026.pdf', 'usr-priya', 0.6, 120, ['compliance'], false],

  ['fld-prj-landmark', 'Landmark Mall Project Charter.pdf', 'usr-mohsin', 2.4, 12, ['landmark', 'project'], false],
  ['fld-prj-landmark', 'Landmark Mall Site Survey.pdf', 'usr-tariq', 9.1, 20, ['landmark'], false],
  ['fld-prj-landmark', 'Landmark Handover Certificate.pdf', 'usr-mohsin', 0.8, 2, ['landmark'], false],
  ['fld-prj-landmark', 'Landmark Mall Progress Report.xlsx', 'usr-tariq', 1.6, 1, ['landmark', 'report'], false],
  ['fld-prj-egoal', 'EGoal Implementation Plan.pdf', 'usr-tariq', 3.3, 5, ['egoal', 'project'], false],
  ['fld-prj-egoal', 'EGoal Training Material.pptx', 'usr-tariq', 14.2, 16, ['egoal', 'training'], false],
  ['fld-prj-egoal', 'EGoal UAT Sign-off.pdf', 'usr-mohsin', 0.7, 3, ['egoal'], false],
  ['fld-prj-stori', 'Stori Deployment Runbook.pdf', 'usr-mohsin', 2.8, 9, ['stori', 'project'], false],
  ['fld-prj-stori', 'Stori Integration Specification.docx', 'usr-rahul', 1.4, 6, ['stori', 'architecture'], false],
  ['fld-prj-stori', 'Stori Store Rollout Tracker.xlsx', 'usr-mohsin', 1.1, 0.4, ['stori', 'report'], false],
  ['fld-projects', 'Project Portfolio Overview.pptx', 'usr-mohsin', 7.9, 4, ['report'], false],
];

const OCR_TEMPLATES = {
  proposal: (n, d) =>
    `INDITECH MIDDLE EAST FZ-LLC\n\n${n.replace(/\.[a-z]+$/i, '')}\n\nPrepared by the ${d} department for the attention of the client procurement committee. This proposal sets out the scope of supply, the commercial offer and the implementation approach.\n\n1. SCOPE OF WORK\nInditech shall supply, install, configure and commission the solution described in Annexure A, including hardware, software licensing and first-year support.\n\n2. COMMERCIAL OFFER\nThe total contract value is stated in Annexure B and is valid for thirty (30) days from the date of issue.\n\n3. PAYMENT TERMS\nPayment terms are forty per cent (40%) on order confirmation, forty per cent (40%) on delivery to site and twenty per cent (20%) on successful commissioning and handover. Invoices are payable within thirty (30) days of the invoice date. Late payment attracts interest at one per cent (1%) per month.\n\n4. DELIVERY SCHEDULE\nDelivery shall commence within four (4) weeks of receipt of a signed purchase order and confirmed advance payment.\n\n5. WARRANTY\nAll supplied equipment carries a twelve (12) month warranty against manufacturing defects from the date of commissioning.`,
  agreement: (n, d) =>
    `AGREEMENT\n\n${n.replace(/\.[a-z]+$/i, '')}\n\nTHIS AGREEMENT is made between Inditech Middle East FZ-LLC ("the Company") and the Client, and is administered by the ${d} department.\n\nCLAUSE 1 — TERM\nThis agreement shall commence on the effective date and remain in force for a period of twenty-four (24) months, renewable by mutual written consent.\n\nCLAUSE 2 — PAYMENT TERMS\nThe Client shall pay all invoices within thirty (30) days of receipt. Payment terms may be revised only by written amendment signed by both parties. Any amount remaining unpaid after the due date shall bear interest at the rate of one per cent (1%) per month.\n\nCLAUSE 3 — SERVICE LEVELS\nThe Company shall respond to critical incidents within four (4) hours and restore service within twenty-four (24) hours.\n\nCLAUSE 4 — CONFIDENTIALITY\nEach party shall keep confidential all information disclosed by the other party and shall not disclose it to any third party without prior written consent.\n\nCLAUSE 5 — TERMINATION\nEither party may terminate this agreement by giving ninety (90) days written notice.\n\nCLAUSE 6 — GOVERNING LAW\nThis agreement shall be governed by the laws of the United Arab Emirates and the courts of Dubai shall have exclusive jurisdiction.`,
  report: (n, d) =>
    `${n.replace(/\.[a-z]+$/i, '').toUpperCase()}\n\nDepartment: ${d}\n\nEXECUTIVE SUMMARY\nThis report consolidates performance for the reporting period and highlights variances against plan. Revenue is tracking ahead of forecast, driven by the GCC retail segment and renewals in the UAE region.\n\nKEY FIGURES\nTotal revenue recognised, gross margin, collections and outstanding receivables are set out in the attached schedules.\n\nOBSERVATIONS\nCollection cycles have improved following the revision of payment terms in customer agreements. Three accounts remain beyond ninety days and are subject to escalation.\n\nRECOMMENDATIONS\nContinue focus on renewal pipeline, complete the Landmark Mall handover documentation, and close outstanding invoices before quarter end.`,
  policy: (n, d) =>
    `INDITECH — ${n.replace(/\.[a-z]+$/i, '').toUpperCase()}\n\nIssued by ${d}. This policy applies to all employees, contractors and third parties acting on behalf of Inditech.\n\n1. PURPOSE\nTo define the standards, responsibilities and procedures that govern this area of company operation.\n\n2. SCOPE\nThis policy applies across all Inditech offices and to all systems operated by or on behalf of the company.\n\n3. RESPONSIBILITIES\nLine managers are responsible for ensuring their teams understand and comply with this policy. Breaches may result in disciplinary action.\n\n4. REVIEW\nThis policy is reviewed annually, or earlier where regulatory change requires it.`,
  generic: (n, d) =>
    `${n.replace(/\.[a-z]+$/i, '')}\n\nDepartment: ${d}\n\nThis document forms part of the Inditech corporate document set. It is maintained by the responsible department and is subject to the company retention policy. Distribution outside Inditech requires approval from the document owner.\n\nRelated records, correspondence and supporting schedules are held in the same folder. For queries regarding content or accuracy, contact the document owner listed in the properties panel.`,
};

function ocrFor(name, deptName, tags) {
  const lower = name.toLowerCase();
  if (tags.includes('agreement') || lower.includes('agreement') || lower.includes('contract')) {
    return OCR_TEMPLATES.agreement(name, deptName);
  }
  if (tags.includes('proposal')) return OCR_TEMPLATES.proposal(name, deptName);
  if (tags.includes('report') || lower.includes('report')) return OCR_TEMPLATES.report(name, deptName);
  if (tags.includes('policy') || lower.includes('policy') || lower.includes('handbook')) {
    return OCR_TEMPLATES.policy(name, deptName);
  }
  return OCR_TEMPLATES.generic(name, deptName);
}

const folderById = Object.fromEntries(folders.map((f) => [f.id, f]));
const deptById = Object.fromEntries(departments.map((d) => [d.id, d]));

export const documents = [];
export const documentVersions = [];

DOC_SPECS.forEach((spec, index) => {
  const [folderId, name, ownerId, sizeMb, modDays, tags = [], confidential = false] = spec;
  const id = `doc-${String(index + 1).padStart(3, '0')}`;
  const folder = folderById[folderId];
  const dept = deptById[folder.departmentId];
  const sizeBytes = Math.round(sizeMb * 1024 * 1024);
  const updatedAt = daysAgo(modDays);
  const createdAt = daysAgo(modDays + 20 + (index % 90));
  const ext = name.slice(name.lastIndexOf('.') + 1).toLowerCase();
  const nonPreviewable = ['zip', 'rar', 'mp4', 'dwg'].includes(ext);
  const versionCount = index % 7 === 0 ? 3 : index % 3 === 0 ? 2 : 1;
  // A few PDFs are "scans" with no text layer — they wait for an OCR engine.
  const scanned = ext === 'pdf' && index % 31 === 9;

  for (let v = 1; v <= versionCount; v += 1) {
    documentVersions.push({
      id: `${id}-v${v}`,
      documentId: id,
      versionNo: v,
      sizeBytes: Math.round(sizeBytes * (0.82 + v * 0.06)),
      uploadedBy: v === versionCount ? ownerId : users[(index + v) % users.length].id,
      changeNote:
        v === 1
          ? 'Initial upload'
          : ['Updated commercial section', 'Corrected typographical errors', 'Revised after client feedback', 'Annexures attached'][
              (index + v) % 4
            ],
      createdAt: daysAgo(modDays + (versionCount - v) * 9),
    });
  }

  documents.push({
    id,
    type: 'document',
    name,
    folderId,
    departmentId: folder.departmentId,
    ownerId,
    sizeBytes,
    mimeType: ext,
    extension: ext,
    versionCount,
    currentVersionNo: versionCount,
    tags,
    isConfidential: confidential,
    description: '',
    ocrStatus: nonPreviewable ? 'not_applicable' : scanned ? 'needs_ocr' : index % 23 === 5 ? 'failed' : index % 19 === 3 ? 'processing' : 'completed',
    ocrText: nonPreviewable || scanned ? '' : ocrFor(name, dept.name, tags),
    createdAt,
    updatedAt,
    deletedAt: null,
    deletedBy: null,
  });
});

/* ------------------------------------------------------------------ deleted set */

['doc-004', 'doc-027', 'doc-052'].forEach((id, i) => {
  const doc = documents.find((d) => d.id === id);
  if (doc) {
    doc.deletedAt = daysAgo([1, 4, 11][i]);
    doc.deletedBy = ['usr-sareesh', 'usr-aisha', 'usr-rahul'][i];
    doc.purgeAfter = daysAhead([29, 26, 19][i]);
  }
});

/* ----------------------------------------------------------------------- shares */

export const shares = [
  { id: 'shr-1', documentId: 'doc-001', sharedBy: 'usr-sareesh', principalType: 'user', principalId: 'usr-mohsin', capabilities: ['view'], createdAt: hoursAgo(3), expiresAt: null, message: 'Please review the commercial section.' },
  { id: 'shr-2', documentId: 'doc-001', sharedBy: 'usr-sareesh', principalType: 'department', principalId: 'dep-marketing', capabilities: ['view', 'download'], createdAt: hoursAgo(20), expiresAt: null, message: '' },
  { id: 'shr-3', documentId: 'doc-006', sharedBy: 'usr-sareesh', principalType: 'user', principalId: 'usr-priya', capabilities: ['view', 'download', 'edit'], createdAt: daysAgo(3), expiresAt: daysAhead(14), message: 'Legal review before renewal.' },
  { id: 'shr-4', documentId: 'doc-020', sharedBy: 'usr-riyas', principalType: 'department', principalId: 'dep-sales', capabilities: ['view', 'download'], createdAt: daysAgo(1), expiresAt: null, message: 'Latest company profile for client meetings.' },
  { id: 'shr-5', documentId: 'doc-070', sharedBy: 'usr-mohsin', principalType: 'user', principalId: 'usr-sareesh', capabilities: ['view', 'download'], createdAt: daysAgo(2), expiresAt: null, message: '' },
  { id: 'shr-6', documentId: 'doc-041', sharedBy: 'usr-anjali', principalType: 'user', principalId: 'usr-sareesh', capabilities: ['view'], createdAt: daysAgo(5), expiresAt: daysAhead(9), message: 'AMC agreement for the Landmark account.' },
  { id: 'shr-7', documentId: 'doc-074', sharedBy: 'usr-tariq', principalType: 'department', principalId: 'dep-it', capabilities: ['view', 'download'], createdAt: daysAgo(4), expiresAt: null, message: '' },
  { id: 'shr-8', documentId: 'doc-018', sharedBy: 'usr-riyas', principalType: 'user', principalId: 'usr-vaisakh', capabilities: ['view', 'download'], createdAt: hoursAgo(9), expiresAt: null, message: 'Q3 numbers for the board pack.' },
  { id: 'shr-9', documentId: 'doc-042', sharedBy: 'usr-fathima', principalType: 'role', principalId: 'role-dept-manager', capabilities: ['view', 'download'], createdAt: daysAgo(18), expiresAt: null, message: '' },
  { id: 'shr-10', documentId: 'doc-002', sharedBy: 'usr-sareesh', principalType: 'user', principalId: 'usr-vaisakh', capabilities: ['view', 'download'], createdAt: hoursAgo(30), expiresAt: null, message: '' },
];

export const externalLinks = [
  {
    id: 'lnk-1',
    documentId: 'doc-020',
    token: 'k7Qm2xP9vLb4TnR8',
    accessType: 'anyone',
    allowedEmails: [],
    capabilities: ['view', 'download'],
    hasPassword: false,
    password: null,
    expiresAt: daysAhead(5),
    maxDownloads: null,
    downloadCount: 14,
    createdBy: 'usr-riyas',
    createdAt: daysAgo(2),
    revokedAt: null,
    lastAccessedAt: hoursAgo(6),
  },
  {
    id: 'lnk-2',
    documentId: 'doc-006',
    token: 'a3Zc8wD1sKf5YhJ2',
    accessType: 'emails',
    allowedEmails: ['procurement@landmarkgroup.example'],
    capabilities: ['view'],
    hasPassword: true,
    password: 'Landmark2026',
    expiresAt: daysAhead(1),
    maxDownloads: 5,
    downloadCount: 2,
    createdBy: 'usr-sareesh',
    createdAt: daysAgo(6),
    revokedAt: null,
    lastAccessedAt: daysAgo(1),
  },
  {
    id: 'lnk-3',
    documentId: 'doc-021',
    token: 'e9Xy4uT7nMq0WgB6',
    accessType: 'anyone',
    allowedEmails: [],
    capabilities: ['view'],
    hasPassword: false,
    password: null,
    expiresAt: daysAgo(3),
    maxDownloads: null,
    downloadCount: 31,
    createdBy: 'usr-riyas',
    createdAt: daysAgo(33),
    revokedAt: null,
    lastAccessedAt: daysAgo(4),
  },
];

/* -------------------------------------------------------------------- favorites */

export const favorites = [
  { userId: 'usr-vaisakh', resourceType: 'document', resourceId: 'doc-001', createdAt: daysAgo(2) },
  { userId: 'usr-vaisakh', resourceType: 'document', resourceId: 'doc-020', createdAt: daysAgo(5) },
  { userId: 'usr-vaisakh', resourceType: 'document', resourceId: 'doc-056', createdAt: daysAgo(9) },
  { userId: 'usr-vaisakh', resourceType: 'folder', resourceId: 'fld-it-security', createdAt: daysAgo(14) },
  { userId: 'usr-vaisakh', resourceType: 'folder', resourceId: 'fld-sales-uae-contracts', createdAt: daysAgo(21) },
  { userId: 'usr-sareesh', resourceType: 'document', resourceId: 'doc-001', createdAt: daysAgo(1) },
  { userId: 'usr-sareesh', resourceType: 'document', resourceId: 'doc-006', createdAt: daysAgo(3) },
  { userId: 'usr-sareesh', resourceType: 'folder', resourceId: 'fld-sales-uae-prop', createdAt: daysAgo(30) },
  { userId: 'usr-riyas', resourceType: 'document', resourceId: 'doc-020', createdAt: daysAgo(4) },
  { userId: 'usr-mohsin', resourceType: 'document', resourceId: 'doc-070', createdAt: daysAgo(6) },
];

/* ----------------------------------------------------------------------- recent */

export const recentItems = [
  { userId: 'usr-vaisakh', documentId: 'doc-001', lastViewedAt: minutesAgo(11) },
  { userId: 'usr-vaisakh', documentId: 'doc-006', lastViewedAt: hoursAgo(2) },
  { userId: 'usr-vaisakh', documentId: 'doc-020', lastViewedAt: hoursAgo(26) },
  { userId: 'usr-vaisakh', documentId: 'doc-056', lastViewedAt: hoursAgo(30) },
  { userId: 'usr-vaisakh', documentId: 'doc-074', lastViewedAt: daysAgo(2) },
  { userId: 'usr-vaisakh', documentId: 'doc-018', lastViewedAt: daysAgo(2) },
  { userId: 'usr-vaisakh', documentId: 'doc-032', lastViewedAt: daysAgo(3) },
  { userId: 'usr-vaisakh', documentId: 'doc-050', lastViewedAt: daysAgo(4) },
  { userId: 'usr-sareesh', documentId: 'doc-001', lastViewedAt: minutesAgo(35) },
  { userId: 'usr-sareesh', documentId: 'doc-009', lastViewedAt: hoursAgo(4) },
  { userId: 'usr-sareesh', documentId: 'doc-016', lastViewedAt: hoursAgo(28) },
  { userId: 'usr-sareesh', documentId: 'doc-041', lastViewedAt: daysAgo(2) },
];

/* ------------------------------------------------------------------ notifications */

export const notifications = [
  { id: 'ntf-1', userId: 'usr-vaisakh', type: 'share', title: 'Riyas Ahmed shared "Sales Report Q3 2026.xlsx" with you', body: 'Q3 numbers for the board pack.', resourceType: 'document', resourceId: 'doc-018', actorId: 'usr-riyas', readAt: null, createdAt: hoursAgo(9) },
  { id: 'ntf-2', userId: 'usr-vaisakh', type: 'upload', title: 'Your upload completed', body: '3 documents were uploaded to Information Technology / Security.', resourceType: 'folder', resourceId: 'fld-it-security', actorId: null, readAt: null, createdAt: hoursAgo(20) },
  { id: 'ntf-3', userId: 'usr-vaisakh', type: 'version', title: '"Access Control Matrix.xlsx" was updated', body: 'Rahul Varma uploaded version 2.', resourceType: 'document', resourceId: 'doc-057', actorId: 'usr-rahul', readAt: null, createdAt: daysAgo(1) },
  { id: 'ntf-4', userId: 'usr-vaisakh', type: 'link', title: 'An external link expires tomorrow', body: '"Landmark Mall Agreement.pdf" link shared with procurement@landmarkgroup.example.', resourceType: 'document', resourceId: 'doc-006', actorId: 'usr-sareesh', readAt: daysAgo(1), createdAt: daysAgo(1) },
  { id: 'ntf-5', userId: 'usr-vaisakh', type: 'ocr', title: 'Text extraction failed', body: '"Penetration Test Report 2026.pdf" could not be processed. Retry from Storage.', resourceType: 'document', resourceId: 'doc-058', actorId: null, readAt: daysAgo(2), createdAt: daysAgo(2) },
  { id: 'ntf-6', userId: 'usr-sareesh', type: 'share', title: 'Anjali Nair shared "Landmark AMC Agreement.pdf" with you', body: 'AMC agreement for the Landmark account.', resourceType: 'document', resourceId: 'doc-041', actorId: 'usr-anjali', readAt: null, createdAt: daysAgo(5) },
  { id: 'ntf-7', userId: 'usr-sareesh', type: 'share', title: 'Mohsin Ali shared "Landmark Mall Project Charter.pdf" with you', body: '', resourceType: 'document', resourceId: 'doc-070', actorId: 'usr-mohsin', readAt: null, createdAt: daysAgo(2) },
];

/* ------------------------------------------------------------------- audit logs */

const AUDIT_ACTIONS = [
  ['DOCUMENT_DOWNLOAD', 'Download'],
  ['DOCUMENT_VIEW', 'View'],
  ['DOCUMENT_UPLOAD', 'Upload'],
  ['DOCUMENT_SHARE', 'Share'],
  ['DOCUMENT_DELETE', 'Delete'],
  ['DOCUMENT_RENAME', 'Rename'],
  ['DOCUMENT_MOVE', 'Move'],
  ['DOCUMENT_VERSION_CREATE', 'Version created'],
  ['USER_LOGIN', 'Login'],
  ['USER_LOGOUT', 'Logout'],
  ['EXTERNAL_LINK_CREATE', 'External link created'],
  ['PERMISSION_CHANGE', 'Permission change'],
];

export const auditLogs = [];
const activeDocs = documents.filter((d) => !d.deletedAt);
for (let i = 0; i < 320; i += 1) {
  const actor = users[(i * 7 + 3) % users.length];
  const [action] = AUDIT_ACTIONS[(i * 5 + 1) % AUDIT_ACTIONS.length];
  const isAuth = action === 'USER_LOGIN' || action === 'USER_LOGOUT';
  const doc = activeDocs[(i * 11 + 5) % activeDocs.length];
  auditLogs.push({
    id: `aud-${i + 1}`,
    actorId: actor.id,
    actorName: actor.name,
    actorEmail: actor.email,
    action,
    resourceType: isAuth ? 'session' : 'document',
    resourceId: isAuth ? null : doc.id,
    resourceName: isAuth ? null : doc.name,
    departmentId: isAuth ? actor.departmentId : doc.departmentId,
    result: i % 37 === 0 ? 'denied' : 'success',
    ipAddress: `10.18.${(i % 6) + 1}.${(i * 13) % 250}`,
    userAgent: i % 3 === 0 ? 'Chrome 129 / Windows' : i % 3 === 1 ? 'Safari 18 / macOS' : 'Edge 129 / Windows',
    createdAt: new Date(now - i * 47 * MIN - (i % 5) * HOUR).toISOString(),
    metadata: action === 'DOCUMENT_SHARE' ? { capabilities: ['view'] } : {},
  });
}

/* -------------------------------------------------------------- system settings */

export const systemSettings = {
  sharing: {
    externalSharingEnabled: true,
    maxLinkExpiryDays: 30,
    requirePasswordForExternal: false,
    allowAnyoneWithLink: true,
  },
  retention: {
    trashRetentionDays: 30,
    maxVersionsPerDocument: 20,
    auditRetentionYears: 7,
  },
  security: {
    sessionIdleMinutes: 30,
    enforceMfaForAdmins: true,
    allowMicrosoftSso: true,
  },
  uploads: {
    maxFileSizeMb: 250,
    allowedExtensions: ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'png', 'jpg', 'jpeg', 'txt', 'csv', 'zip'],
  },
  features: {
    aiSearch: true,
    ocr: true,
    thumbnails: true,
  },
  storage: {
    totalQuotaBytes: 1024 * 1024 * 1024 * 1024, // 1 TB
  },
};

export const organization = {
  id: 'org-inditech',
  name: 'Inditech Middle East',
  slug: 'inditech',
};
